import React, { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { normaliseReviewPoints, REVIEW_POINTS_DEFAULTS } from '../lib/bradford';
import { useAuth } from '../hooks/useAuth';
import { applyBrandAccent } from '../lib/theme';
import { useDocument } from '../hooks/useFirestore';
import { schoolDoc, schoolPrefsDoc, pastoralConfigDoc, sendConfigDoc, staffAbsenceConfigDoc, termDatesDoc } from '../lib/firestore';
import { MODULES } from '../modules/registry';
import { HOME_SECTIONS } from '../modules/homeSections';
import type { BehaviourFeatureId, HomeSectionId, ModuleId, PastoralConfig, PastoralFeatureId, School, SchoolPrefs, SendConfig, StaffAbsenceConfig, Term, TermDates } from '../types';

/** Every hideable Behaviour & Pastoral blade, in sidebar order. */
export const BEHAVIOUR_FEATURES: readonly BehaviourFeatureId[] = ['detentions', 'on-call', 'ool', 'isolation', 'one-way', 'lates', 'overview'];
/** The pastoral subset — still surfaced for pastoral-specific consumers. */
export const PASTORAL_FEATURES: readonly PastoralFeatureId[] = ['ool', 'isolation', 'one-way', 'lates'];

/**
 * What the signed-in user may see in the SEND module.
 *
 *   senco      — named SENCo: full access, and the only person who may change
 *                who else has it
 *   team       — on the SEND team: full access
 *   compliance — named leadership: the register and the review/provision
 *                compliance picture, never the case itself
 *   classroom  — everyone else: their own pupils' support summaries only
 *
 * This is a COSMETIC derivation, used to decide what to render. Every real
 * grant is re-checked in firestore.rules on each direct read and in the
 * callable on each write — the same contract as isMorningLateOfficer.
 */
export type SendTier = 'senco' | 'team' | 'compliance' | 'classroom';

interface SchoolContextValue {
  school: (School & { id: string }) | null;
  schoolLoading: boolean;
  /** Module ids visible to the school: entitled AND not hidden by the school admin */
  activeModules: ModuleId[];
  hasModule: (id: ModuleId) => boolean;
  /** Module ids the school is entitled to, ignoring the admin's hide toggles —
   *  drives the Settings page so hidden modules can be switched back on. */
  subscribedModules: ModuleId[];
  /** Modules the school admin has switched off for their school */
  hiddenModules: ModuleId[];
  /** Behaviour & Pastoral features the school admin has switched off */
  hiddenBehaviourFeatures: BehaviourFeatureId[];
  /** Behaviour & Pastoral features still visible — entitlement stays on the module */
  activeBehaviourFeatures: BehaviourFeatureId[];
  /** Pastoral subset still visible (derived) — for pastoral-specific consumers */
  activePastoralFeatures: PastoralFeatureId[];
  /** Home cards the school admin has switched off for the whole school */
  hiddenHomeSections: HomeSectionId[];
  /** True when the school has switched off the daily morning briefing email.
   *  Nothing in the app renders differently — the digest job reads it — but the
   *  Settings toggle needs the current value. */
  morningBriefingOff: boolean;
  /** Home cards to render, in registry order. Module and role gating is applied
   *  by Home itself, which knows the signed-in user's role. */
  activeHomeSections: HomeSectionId[];
  /** True if the signed-in user is a designated Morning Late officer (or super
   *  admin) — the only people who may record a morning late. */
  isMorningLateOfficer: boolean;
  /** On pastoralConfig.detentionStaffUids — runs the Head Teacher detention
   *  register. Cosmetic, like isMorningLateOfficer: the rules re-check it. */
  isDetentionStaff: boolean;
  /** Cosmetic — see SendTier. Never the basis of an access decision. */
  sendTier: SendTier;
  /** The SEND module is on but no SENCo has been named, so nobody can use it. */
  sendNeedsSetup: boolean;
  /** Cosmetic, like sendTier: on the staff absence viewer list (or super
   *  admin). Every report callable re-checks the list on the server. */
  isStaffAbsenceViewer: boolean;
  /** The Staff Absence module is on but nobody has been named to see it. */
  staffAbsenceNeedsSetup: boolean;
  /** Which measure the school's absence policy uses — Bradford scores or
   *  KCSP-style review points. Cosmetic: decides which page the nav and the
   *  person page lead with. Both pages stay reachable. */
  staffAbsenceMeasure: 'bradford' | 'reviewPoints';
  /** The rolling period review points are counted over (months). */
  staffAbsenceWindowMonths: number;
  /** The school's term dates — from Settings, or the legacy Behaviour copy
   *  until the admin saves the new card. Sorted by start. */
  terms: Term[];
  /** yyyy-mm-dd INSET days from Settings. */
  insetDays: string[];
  termDatesSource: 'settings' | 'behaviour' | 'none';
}

const SchoolContext = createContext<SchoolContextValue | null>(null);

function isEntitled(school: School | null, id: ModuleId): boolean {
  const ent = school?.modules?.[id];
  if (!ent?.active) return false;
  if (ent.expiresAt && ent.expiresAt.toMillis() < Date.now()) return false;
  return true;
}

/**
 * Single shared subscription to the signed-in user's school document.
 * Drives module entitlement checks everywhere (launcher, sidebar, ModuleRoute).
 * Super admins have no schoolId — they see everything regardless.
 *
 * Visibility = subscription (school doc, super-admin controlled) minus the
 * school admin's own hide toggles (schoolPrefs doc). Hiding never changes
 * what the school pays for — it only tailors what staff and pupils see.
 */
export function SchoolProvider({ children }: { readonly children: ReactNode }) {
  const { appUser } = useAuth();

  const { data: school, loading: rawLoading, error } = useDocument<School>(
    appUser?.schoolId ? schoolDoc(appUser.schoolId) : null,
  );
  const { data: prefs } = useDocument<SchoolPrefs>(
    appUser?.schoolId ? schoolPrefsDoc(appUser.schoolId) : null,
  );
  // Readable by every member, so this costs one document and is live: a SENCo
  // removed from the team loses the nav on their next render, where a custom
  // claim would have left it there until their token refreshed.
  const { data: sendCfg, loading: sendCfgLoading } = useDocument<SendConfig>(
    appUser?.schoolId ? sendConfigDoc(appUser.schoolId) : null,
  );
  const { data: pastoral } = useDocument<PastoralConfig>(
    appUser?.schoolId ? pastoralConfigDoc(appUser.schoolId) : null,
  );
  // Same contract as sendCfg: readable by every member, one live document.
  const { data: absenceCfg, loading: absenceCfgLoading } = useDocument<StaffAbsenceConfig>(
    appUser?.schoolId ? staffAbsenceConfigDoc(appUser.schoolId) : null,
  );
  // Term dates and INSET days, set by the school admin in Settings.
  const { data: termDates } = useDocument<TermDates>(
    appUser?.schoolId ? termDatesDoc(appUser.schoolId) : null,
  );

  const value = useMemo<SchoolContextValue>(() => {
    // The user has a school but its doc hasn't arrived yet — even if the
    // subscription hasn't technically started (the render between auth
    // resolving and useDocument's effect firing). Without this, ModuleRoute
    // sees "no modules" for one render and bounces the user to /home.
    const schoolLoading = !!appUser?.schoolId && !school && !error ? true : rawLoading;

    const all: ModuleId[] = MODULES.map((m) => m.id);
    const subscribedModules = appUser?.role === 'superAdmin'
      ? all
      : all.filter((id) => isEntitled(school, id));
    const hiddenModules = (prefs?.hiddenModules ?? []).filter((id): id is ModuleId =>
      (all as string[]).includes(id),
    );
    // Super admins bypass hide toggles so they can always inspect a school.
    const activeModules = appUser?.role === 'superAdmin'
      ? all
      : subscribedModules.filter((id) => !hiddenModules.includes(id));

    // New unified field is authoritative; fall back to the legacy pastoral-only
    // field so a school that only ever hid pastoral tools keeps them hidden
    // until the admin next saves (which writes hiddenBehaviourFeatures).
    const rawHidden = prefs?.hiddenBehaviourFeatures ?? prefs?.hiddenPastoralFeatures ?? [];
    const hiddenBehaviourFeatures = rawHidden.filter(
      (id): id is BehaviourFeatureId => (BEHAVIOUR_FEATURES as readonly string[]).includes(id),
    );
    const activeBehaviourFeatures = appUser?.role === 'superAdmin'
      ? [...BEHAVIOUR_FEATURES]
      : BEHAVIOUR_FEATURES.filter((id) => !hiddenBehaviourFeatures.includes(id));
    // Pastoral subset, derived, so existing pastoral-only consumers keep working.
    const activePastoralFeatures = activeBehaviourFeatures.filter(
      (id): id is PastoralFeatureId => (PASTORAL_FEATURES as readonly string[]).includes(id),
    );

    // Home cards: on unless the school admin hid them — same contract as
    // hiddenModules and hiddenPastoralFeatures, so a card added in a later
    // release appears for everyone without needing a migration.
    const allSectionIds = HOME_SECTIONS.map((s) => s.id);
    const hiddenHomeSections = (prefs?.hiddenHomeSections ?? []).filter(
      (id): id is HomeSectionId => (allSectionIds as string[]).includes(id),
    );
    const activeHomeSections = appUser?.role === 'superAdmin'
      ? allSectionIds
      : allSectionIds.filter((id) => !hiddenHomeSections.includes(id));

    // Morning Late officers are matched by the user's linked MIS staff id, so an
    // officer's account must be linked in Staff management to record lates.
    const officerWondeIds = (pastoral?.morningLateStaff ?? []).map((s) => s.wondeId);
    const isMorningLateOfficer = appUser?.role === 'superAdmin'
      || (!!appUser?.wondeStaffId && officerWondeIds.includes(appUser.wondeStaffId));
    // A list, not a role: being head or admin is not a place on it.
    const isDetentionStaff = !!appUser?.id
      && (pastoral?.detentionStaffUids ?? []).includes(appUser.id);

    // The SEND tier is a config membership, never a role — the role claim is
    // single-valued and a SENCo is usually also a class teacher. Order matters:
    // the most privileged match wins, so someone on both the SEND team and the
    // leadership viewer list is treated as SEND team.
    const uid = appUser?.id ?? '';
    const sendTier: SendTier = appUser?.role === 'superAdmin' ? 'senco'
      : (sendCfg?.sencoUids ?? []).includes(uid) ? 'senco'
      : (sendCfg?.sendTeamUids ?? []).includes(uid) ? 'team'
      : (sendCfg?.leadershipViewerUids ?? []).includes(uid) ? 'compliance'
      : 'classroom';
    // A school whose admin switched the module on but never named a SENCo.
    //
    // A MISSING config counts, not just an empty one. setSchoolModules seeds the
    // document, but a school entitled some other way has none at all — and the
    // first version of this required the document to exist, so those schools got
    // "ask your SENCo to add you" shown to the very person whose job it is to
    // appoint one. A dead end with no way out of it on screen.
    //
    // Guarded on loading, or every SEND page flashes "not set up" for a moment
    // on each navigation while the document is in flight.
    const sendNeedsSetup = activeModules.includes('send')
      && !sendCfgLoading
      && (sendCfg?.sencoUids ?? []).length === 0;

    // Staff absence: a list the school admin keeps, never a role — being school
    // admin is deliberately not a pass, so an admin sees the gate until they
    // name themselves.
    const isStaffAbsenceViewer = appUser?.role === 'superAdmin'
      || (absenceCfg?.viewerUids ?? []).includes(uid);
    const staffAbsenceNeedsSetup = activeModules.includes('staffAbsence')
      && !absenceCfgLoading
      && (absenceCfg?.viewerUids ?? []).length === 0;
    const staffAbsenceMeasure: 'bradford' | 'reviewPoints' = absenceCfg?.measure === 'reviewPoints' ? 'reviewPoints' : 'bradford';
    const staffAbsenceWindowMonths = normaliseReviewPoints(absenceCfg?.reviewPoints)?.windowMonths ?? REVIEW_POINTS_DEFAULTS.windowMonths;

    // Settings wins; the Behaviour copy is the fallback for a school that has
    // not opened the new card yet.
    const cleanTerms = (list: Term[] | undefined): Term[] => (list ?? [])
      .filter((t) => typeof t?.start === 'string' && typeof t?.end === 'string' && t.start <= t.end)
      .sort((a, b) => a.start.localeCompare(b.start));
    const settingsTerms = cleanTerms(termDates?.terms);
    const legacyTerms = cleanTerms(pastoral?.terms);
    const termDatesSource: 'settings' | 'behaviour' | 'none' = settingsTerms.length > 0 ? 'settings' : legacyTerms.length > 0 ? 'behaviour' : 'none';
    const terms = termDatesSource === 'settings' ? settingsTerms : legacyTerms;
    const insetDays = termDatesSource === 'settings' ? [...(termDates?.insetDays ?? [])].sort() : [];

    return {
      school: school ?? null,
      schoolLoading,
      activeModules,
      hasModule: (id) => activeModules.includes(id),
      subscribedModules,
      hiddenModules,
      hiddenBehaviourFeatures,
      activeBehaviourFeatures,
      activePastoralFeatures,
      hiddenHomeSections,
      activeHomeSections,
      morningBriefingOff: prefs?.morningBriefingOff === true,
      isMorningLateOfficer,
      isDetentionStaff,
      sendTier,
      sendNeedsSetup,
      isStaffAbsenceViewer,
      staffAbsenceNeedsSetup,
      staffAbsenceMeasure,
      staffAbsenceWindowMonths,
      terms,
      insetDays,
      termDatesSource,
    };
  }, [appUser?.role, appUser?.id, appUser?.schoolId, appUser?.wondeStaffId, school, prefs, pastoral, sendCfg, sendCfgLoading, absenceCfg, absenceCfgLoading, termDates, rawLoading, error]);

  // Re-theme the whole app to the school's accent colour (or back to the
  // SchoolOne default when unset).
  useEffect(() => {
    applyBrandAccent(school?.branding?.accentColor ?? null);
  }, [school?.branding?.accentColor]);

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
}

export function useSchool(): SchoolContextValue {
  const ctx = useContext(SchoolContext);
  if (!ctx) throw new Error('useSchool must be used within SchoolProvider');
  return ctx;
}
